import { Component, OnInit, OnDestroy, AfterViewInit, OnChanges, SimpleChanges, Input, ViewChild, ElementRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { ReportService } from '../_services/report-service';
import { OrgService } from '../_services/org.service';
import { NgbCalendar } from '@ng-bootstrap/ng-bootstrap';
import { LoaderService } from '../loader/loader.service';
import { DatePipe } from '@angular/common';
import { PhaseService } from 'src/app/_services/phase.service';
import * as c3 from 'c3';
import * as d3 from 'd3';

@Component({
	selector: 'app-raw-balloon',
	templateUrl: './raw-balloon.component.html',
	styleUrls: ['./raw-balloon.component.css'],
})
export class RawBalloonComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
	@ViewChild('gradeChartContainer', { static: false }) gradeChartContainer: ElementRef;
	@Input() node?: any;
	private dataSubscription: Subscription; // Subscription to handle data updates
	gradeChart: any;

	// Model for filter inputs
	filterModel = {
		node: { id: null, isSelected: true, name: null },
		date: { value: null },
		cohort: { isSelected: true },
		includeNoneAssessed: true,
		characteristics: {},
		type: 'raw',
	};
	private holdInterval: any;
	private readonly HOLD_DELAY = 200;
	calDate = this.calendar.getToday(); // Current date
	loading: any = true; // Loading state

	phases = []; // List of phases
	data: any; // Hold all report Data (will be set to calculatedData.phases)
	headers = ['Emerging', 'Bronze', 'Silver', 'Gold', 'Platinum']; // Headers for the table
	subscriptions: Subscription[] = [];
	currentHistoryIndex = 0;
	anyNotSelected: boolean = false;
	notAssessed: number = 0;
	notAccessedCount: number = 0;
	totalStudentCount: number = 0;
	previousNotAccessedCount: number;
	done: any;
	completedPercent: number;
	readingAchievementHistory: any[] = [];
	currentHistory: any;
	selectedHistory: boolean;

	public includeNotAssessed: boolean = true;
	public calculatedData: any = null;
	private originalData: any = null;
	constructor(
		private datePipe: DatePipe,
		private reportService: ReportService,
		public LoaderService: LoaderService,
		private orgService: OrgService,
		private calendar: NgbCalendar,
		private phaseService: PhaseService
	) {}

	clearVariables(): void {
		this.subscriptions.forEach((sub) => sub.unsubscribe());
		this.subscriptions = [];
		this.anyNotSelected = false;
		this.notAssessed = 0;
		this.notAccessedCount = 0;
		this.previousNotAccessedCount = null;
		this.totalStudentCount = 0;
		this.originalData = null;
		this.calculatedData = null;
		this.data = null;
	}

	getReadingAchievementHistory(nodeId: number) {
		this.reportService.getReadingAchievementHistory(nodeId).subscribe((data) => {
			console.log('this.readingAchievementHistory', data);
			this.readingAchievementHistory = data;
		});

		console.log('readingAchievementHistory', this.readingAchievementHistory);
	}

	changeHistory(i) {
		this.currentHistory = this.readingAchievementHistory[i];
		this.selectedHistory = true;
		console.log('currentHistory', this.currentHistory);
		//map the data to the current data
		const suffix = ['_Percentage', '_Value'];
		const phasePrefixes = ['PI', 'PII', 'PIII'];
		const divisionTypes = ['Emerging', 'Bronze', 'Silver', 'Gold', 'Platinum'];

		phasePrefixes.forEach((prefix, phaseIndex) => {
			const phaseId = 1011 + phaseIndex;

			//loop suffix adn set raw and percentage
			divisionTypes.forEach((type, divIndex) => {
				const key = `${prefix}_${type}_Value`;
				console.log('this.currentHistory[key]', key + ' ' + this.currentHistory[key]);
				this.data[phaseId].current.divisions.raw[4 - divIndex] = this.currentHistory[key];
			});
			divisionTypes.forEach((type, divIndex) => {
				const key = `${prefix}_${type}_Percentage`;
				console.log('this.currentHistory[key]', key + ' ' + this.currentHistory[key]);
				this.data[phaseId].current.divisions.percentage[4 - divIndex] = this.currentHistory[key];
			});
		});
	}

	ngOnDestroy(): void {
		this.dataSubscription?.unsubscribe();
		if (this.gradeChart) {
			this.gradeChart.destroy();
		}
	}

	handleFilterChange(data: any) {
		this.filterModel.characteristics = {
			...this.filterModel.characteristics,
			...data,
		};

		this.anyNotSelected = false;

		Object.keys(this.filterModel.characteristics).forEach((key) => {
			if (this.filterModel.characteristics[key].some((obj) => obj.isSelected !== true)) {
				this.anyNotSelected = true;
			}
		});

		this.setCharacteristic();
		this.updateTotal();
	}

	reset() {
		this.selectAllCharacteristics();
		this.filterModel.includeNoneAssessed = true;
		this.filterModel.cohort.isSelected = true;
		this.anyNotSelected = false;
	}

	toggleNoneAssessed() {
		// Toggle the includeNoneAssessed flag
		this.filterModel.includeNoneAssessed = !this.filterModel.includeNoneAssessed;
		this.includeNotAssessed = this.filterModel.includeNoneAssessed;

		// Recalculate from original data
		this.recalculateData();

		// Reapply current filter state after recalculation
		// This ensures filters are respected even when toggling "Include Not Assessed"
		this.setCharacteristic();
		this.updateTotal();

		// Update chart after recalculation
		setTimeout(() => {
			this.drawGradeChart();
		}, 100);
	}

	updateTotal() {
		if (!this.data) return;

		// Update completed total students percentage
		if (this.data['complete']?.current) {
			const completeCount = this.countCompleteNotExcluded();
			this.data['complete'].current.percentage = this.calculatePercentage(completeCount);
		}

		// Recalculate percentages for each phase division
		this.phases.forEach((phase) => {
			const phaseData = this.data[phase.assessmentBookId];
			if (phaseData?.current?.divisions) {
				// Ensure raw and data arrays are in sync
				if (!phaseData.current.divisions.raw) {
					phaseData.current.divisions.raw = [...phaseData.current.divisions.data];
				}
				if (!phaseData.current.divisions.percentage) {
					phaseData.current.divisions.percentage = new Array(5).fill(0);
				}

				// Update data array to match raw array
				for (let i = 0; i < 5; i++) {
					const rawValue = phaseData.current.divisions.raw[i] ?? phaseData.current.divisions.data[i] ?? 0;
					phaseData.current.divisions.data[i] = rawValue;
					phaseData.current.divisions.raw[i] = rawValue;
					// Recalculate percentage
					phaseData.current.divisions.percentage[i] = this.calculatePercentage(rawValue);
				}
			}
		});
	}

	selectAllCharacteristics(): void {
		const updatedCharacteristics = {};
		Object.keys(this.filterModel.characteristics).forEach((key) => {
			updatedCharacteristics[key] = this.filterModel.characteristics[key].map((char) => ({
				...char,
				isSelected: true,
			}));
		});

		this.filterModel.characteristics = updatedCharacteristics;
		this.setCharacteristic();
		//this.updateTotal();
		//	this.cdr.detectChanges();
	}

	ngOnInit(): void {
		this.onCalDateChange();
		this.loadPhases();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.node) {
			if (this.dataSubscription) {
				this.dataSubscription.unsubscribe();
				this.dataSubscription = null;
			}
			this.clearVariables();
			const current = changes.node.currentValue;
			if (!current || !current.id) {
				this.filterModel.node = { id: null, isSelected: false, name: null };
				this.loading = false;
				return;
			}
			this.filterModel.node = current;
			this.loadData();
		}
	}

	countByCharacteristic(): { [characteristic: string]: { [value: string]: number } } {
		const counts: { [characteristic: string]: { [value: string]: number } } = {};
		let naCount = 0; // Initialize count for "N/A"

		for (const key in this.data) {
			if (this.data.hasOwnProperty(key)) {
				const students = this.data[key].current.students;
				for (const student in students) {
					if (students.hasOwnProperty(student)) {
						const studentTargetPhase = key == 'complete' ? 1013 : key;

						if (!students[student].excluded && students[student].currentPhase == studentTargetPhase) {
							const characteristics = students[student].characteristics;
							let hasCharacteristics = false;

							for (const characteristic in this.filterModel.characteristics) {
								if (this.filterModel.characteristics.hasOwnProperty(characteristic)) {
									if (!counts[characteristic]) {
										counts[characteristic] = {};
									}

									if (characteristics.hasOwnProperty(characteristic) && characteristics[characteristic] !== '') {
										const characteristicValue = characteristics[characteristic];
										if (!counts[characteristic][characteristicValue]) {
											counts[characteristic][characteristicValue] = 0;
										}
										counts[characteristic][characteristicValue]++;
										hasCharacteristics = true;
									}
								}
							}

							// If the student does not have any characteristic values, increment the "N/A" count and set "N/A" for each characteristic
							if (!hasCharacteristics) {
								naCount++;
								for (const characteristic in this.filterModel.characteristics) {
									if (this.filterModel.characteristics.hasOwnProperty(characteristic)) {
										students[student].characteristics[characteristic] = 'N/A';
									}
								}
							}
						}
					}
				}
				// Update the students in this.data with the modified students
				this.data[key].current.students = students;
			}
		}

		// Add the "N/A" count to the counts object
		for (const characteristic in this.filterModel.characteristics) {
			if (this.filterModel.characteristics.hasOwnProperty(characteristic)) {
				if (!counts[characteristic]) {
					counts[characteristic] = {};
				}
				counts[characteristic]['N/A'] = naCount;
			}
		}

		console.log('Final Counts:', counts);
		return counts;
	}

	loadPhases() {
		this.phaseService.phases$.subscribe(
			(data) => {
				console.log('Phases:', data);
				this.phases = data.reverse();
			},
			(error) => {
				console.error('Error fetching phases:', error);
			}
		);
	}

	nodeChange(node: any): void {
		// Handle null case (when clearSelection is called)
		if (!node || !node.id) {
			// Cancel any ongoing requests
			this.cancelRequest();
			// Clear the data and reset state
			this.clearVariables();
			this.filterModel.node = { id: null, isSelected: false, name: null };
			this.loading = false;
			return;
		}

		this.filterModel.node = node;
		// Automatically load data when node changes (same as reading-achievement-phases)
		if (node?.id) {
			this.loadData();
		}
	}

	calculatePercentage(value: number): number {
		// Calculate total participants across all phases (sum of all divisions)
		let totalParticipants = 0;

		if (this.data) {
			this.phases.forEach((phase) => {
				const phaseData = this.data[phase.assessmentBookId];
				if (phaseData?.current?.divisions?.data) {
					const sumOfDivisions = phaseData.current.divisions.data.reduce(
						(sum: number, val: number) => sum + (val || 0),
						0
					);
					totalParticipants += sumOfDivisions;
				}
			});

			// Add complete phase count if it exists (count directly to avoid circular call)
			if (this.data['complete']?.current?.students) {
				const completeStudents = this.data['complete'].current.students;
				let completeCount = 0;
				for (const student in completeStudents) {
					if (completeStudents.hasOwnProperty(student) && !completeStudents[student].excluded) {
						completeCount++;
					}
				}
				totalParticipants += completeCount;
			}
		}

		// Calculate percentage based on total participants
		const result = totalParticipants > 0 ? parseFloat(((value / totalParticipants) * 100).toFixed(2)) : 0;
		return isNaN(result) || result === -Infinity ? 0 : result;
	}

	onCalDateChange() {
		let date = new Date(this.calDate.year, this.calDate.month - 1, this.calDate.day);
		let formateDate = this.datePipe.transform(date, 'yyyy-MM-dd');
		this.filterModel.date.value = formateDate;
	}

	getColorClass(index: number): string {
		const colorClasses = ['bg-5', 'bg-4', 'bg-3', 'bg-2', 'bg-1'];
		return colorClasses[index] || '';
	}
	getColorHeaderClass(index: number): string {
		const colorHeaderClasses = ['labelcolor-1', 'labelcolor-2', 'labelcolor-3', 'labelcolor-4', 'labelcolor-5'];
		return colorHeaderClasses[index] || '';
	}

	loadData(): void {
		this.selectedHistory = false;
		this.currentHistory = null;
		this.clearVariables();
		const currentNodeId = this.filterModel?.node?.id;

		if (!currentNodeId) {
			return;
		}

		this.loading = true;
		this.dataSubscription?.unsubscribe();

		// Use getReadingAchievementPhases like reading-achievement-phases component
		this.dataSubscription = this.reportService.getReadingAchievementPhases(currentNodeId).subscribe({
			next: (rawData: any) => {
				//this.getReadingAchievementHistory(currentNodeId);

				// Store original data (deep copy to preserve it)
				this.originalData = JSON.parse(JSON.stringify(rawData));

				// Sync includeNotAssessed with filterModel
				this.includeNotAssessed = this.filterModel.includeNoneAssessed;

				// Calculate percentages from raw data
				this.calculatedData = this.calculatePercentages(rawData, this.includeNotAssessed);

				// Update data with calculated values so template can access them
				// calculatedData.phases includes all phases (1011, 1012, 1013) and complete
				this.data = this.calculatedData.phases;

				// Handle characteristics if available in the response
				if (rawData.characteristics) {
					this.filterModel.characteristics = rawData.characteristics;
					let counts = this.countByCharacteristic();
					const updatedCharacteristics = {};
					this.countStudents();
					this.countNotAssessedStudents();
					Object.keys(this.filterModel.characteristics).forEach((key) => {
						updatedCharacteristics[key] = this.filterModel.characteristics[key].map((char) => ({
							...char,
							isSelected: true,
							count: counts[key]?.[char.name] || 0, // Append the count
						}));

						updatedCharacteristics[key].push({
							name: 'N/A',
							characteristicBelongs: '1',
							isSelected: true,
							count: counts[key]?.['N/A'] || 0,
						});
					});

					this.filterModel.characteristics = updatedCharacteristics;
				}

				this.notAssessed = this.getNotAssessedCount('current');
				this.loading = false;
				this.LoaderService.setLoadingGrowthChartRaw(false);
				this.LoaderService.setLoadingGrowthChart(false); // Reset the loader set by getReadingAchievementPhases

				// Initialize grade chart after data is loaded
				setTimeout(() => {
					this.drawGradeChart();
				}, 100);
			},
			error: (error) => {
				this.loading = false;
				this.LoaderService.setLoadingGrowthChartRaw(false);
				this.LoaderService.setLoadingGrowthChart(false); // Reset the loader set by getReadingAchievementPhases
				console.error('Error fetching data:', error);
			},
		});
	}

	countNotAssessedStudents(): number {
		// Count not-assessed students that are NOT excluded by filters
		// Apply the same filtering logic as setCharacteristic()
		if (!this.originalData?.notAssessedCounts?.['current']?.['students']) {
			return 0;
		}

		const notAssessedStudents = this.originalData.notAssessedCounts['current']['students'];
		let count = 0;

		// Check each not-assessed student to see if they match current filter selections
		for (const studentId in notAssessedStudents) {
			if (notAssessedStudents.hasOwnProperty(studentId)) {
				const student = notAssessedStudents[studentId];
				const studentChar = student.characteristics || {};

				// Apply the same exclusion logic as setCharacteristic()
				let shouldBeExcluded = false;

				// Check each characteristic filter
				for (const char in this.filterModel.characteristics) {
					const characteristics = this.filterModel.characteristics[char];
					if (characteristics) {
						const selectedOptions = characteristics.filter((opt: any) => opt.isSelected);

						// If no options are selected for this characteristic, exclude all students
						if (selectedOptions.length === 0) {
							shouldBeExcluded = true;
							break;
						}

						// Check if student's characteristic value matches any selected option
						const studentValue = studentChar[char];
						if (studentValue && studentValue !== '' && studentValue !== null && studentValue !== undefined) {
							// Student has a value - check if it matches selected options
							const hasMatchingOption = selectedOptions.some((opt: any) => opt.name === studentValue);
							if (!hasMatchingOption) {
								shouldBeExcluded = true;
								break;
							}
						} else {
							// Student doesn't have this characteristic or it's empty - check if 'N/A' is selected
							const hasNASelected = selectedOptions.some((opt: any) => opt.name === 'N/A');
							if (!hasNASelected) {
								shouldBeExcluded = true;
								break;
							}
						}
					}
				}

				if (!shouldBeExcluded) {
					count++;
				}
			}
		}

		this.notAssessed = count;
		return count;
	}

	countStudents() {
		// Count all non-excluded students (assessed + not-assessed)
		// Total should always include both, regardless of "Include Not Assessed" toggle
		// Calculate the same way as reading-achievement-phases component
		const assessedCount = this.getAssessedCount();
		const notAssessedCount = this.countNotAssessedStudents();
		const total = assessedCount + notAssessedCount;
		this.totalStudentCount = total;
		return total;
	}

	// Helper method to get count of assessed students only (non-excluded, not notAssessed)
	// Calculate from division sums like reading-achievement-phases component
	getAssessedCount(): number {
		let assessedCount = 0;
		if (this.data) {
			// Sum all phase divisions (1011, 1012, 1013) - matches reading-achievement-phases logic
			for (const key in this.data) {
				if (this.data.hasOwnProperty(key) && key !== 'complete' && key !== '_meta') {
					const phaseData = this.data[key];
					if (phaseData?.current?.divisions?.data) {
						const sumOfDivisions = phaseData.current.divisions.data.reduce(
							(sum: number, val: number) => sum + (val || 0),
							0
						);
						assessedCount += sumOfDivisions;
					}
				}
			}

			// Add complete phase count - matches reading-achievement-phases logic
			if (this.data['complete']?.current?.students) {
				// Count non-excluded students in complete phase
				const completeStudents = this.data['complete'].current.students;
				let completeCount = 0;
				for (const student in completeStudents) {
					if (completeStudents.hasOwnProperty(student) && !completeStudents[student].excluded) {
						completeCount++;
					}
				}
				assessedCount += completeCount;
			} else if (this.data['complete']?.current?.count) {
				assessedCount += this.data['complete'].current.count || 0;
			}

			// If includeNotAssessed is true, not-assessed students were added to phase 1011's emerging division (index 4)
			// Subtract them from assessed count to get only assessed students
			if (this.filterModel.includeNoneAssessed && this.data[1011]?.current?.divisions?.data) {
				const notAssessedCount = this.countNotAssessedStudents();
				assessedCount -= notAssessedCount;
			}
		}
		return assessedCount;
	}

	setCharacteristic() {
		if (!this.data) return;

		// Step 1: Set excluded flags on all students based on current filter selections
		// Process regular phases (1011, 1012, 1013)
		for (const key in this.data) {
			if (this.data.hasOwnProperty(key) && key !== 'complete' && key !== '_meta') {
				const students = this.data[key].current.students;
				if (students) {
					for (const studentId in students) {
						if (students.hasOwnProperty(studentId)) {
							const student = students[studentId];
							const studentChar = student.characteristics || {};

							// Check if student should be excluded based on all characteristic filters
							let shouldBeExcluded = false;

							// Check each characteristic filter
							for (const char in this.filterModel.characteristics) {
								const characteristics = this.filterModel.characteristics[char];
								if (characteristics) {
									const selectedOptions = characteristics.filter((opt: any) => opt.isSelected);

									// If no options are selected for this characteristic, exclude all students
									if (selectedOptions.length === 0) {
										shouldBeExcluded = true;
										break;
									}

									// Check if student's characteristic value matches any selected option
									const studentValue = studentChar[char];
									if (studentValue && studentValue !== '' && studentValue !== null && studentValue !== undefined) {
										// Student has a value - check if it matches selected options
										const hasMatchingOption = selectedOptions.some((opt: any) => opt.name === studentValue);
										if (!hasMatchingOption) {
											shouldBeExcluded = true;
											break;
										}
									} else {
										// Student doesn't have this characteristic or it's empty - check if 'N/A' is selected
										const hasNASelected = selectedOptions.some((opt: any) => opt.name === 'N/A');
										if (!hasNASelected) {
											shouldBeExcluded = true;
											break;
										}
									}
								}
							}

							// Set excluded flag
							student.excluded = shouldBeExcluded;
						}
					}
				}
			}
		}

		// Step 1b: Also process complete phase students with the same filtering logic
		if (this.data['complete']?.current?.students) {
			const completeStudents = this.data['complete'].current.students;
			for (const studentId in completeStudents) {
				if (completeStudents.hasOwnProperty(studentId)) {
					const student = completeStudents[studentId];
					const studentChar = student.characteristics || {};

					// Check if student should be excluded based on all characteristic filters
					let shouldBeExcluded = false;

					// Check each characteristic filter
					for (const char in this.filterModel.characteristics) {
						const characteristics = this.filterModel.characteristics[char];
						if (characteristics) {
							const selectedOptions = characteristics.filter((opt: any) => opt.isSelected);

							// If no options are selected for this characteristic, exclude all students
							if (selectedOptions.length === 0) {
								shouldBeExcluded = true;
								break;
							}

							// Check if student's characteristic value matches any selected option
							const studentValue = studentChar[char];
							if (studentValue && studentValue !== '' && studentValue !== null && studentValue !== undefined) {
								// Student has a value - check if it matches selected options
								const hasMatchingOption = selectedOptions.some((opt: any) => opt.name === studentValue);
								if (!hasMatchingOption) {
									shouldBeExcluded = true;
									break;
								}
							} else {
								// Student doesn't have this characteristic or it's empty - check if 'N/A' is selected
								const hasNASelected = selectedOptions.some((opt: any) => opt.name === 'N/A');
								if (!hasNASelected) {
									shouldBeExcluded = true;
									break;
								}
							}
						}
					}

					// Set excluded flag
					student.excluded = shouldBeExcluded;
				}
			}
		}

		// Step 2: Recalculate division counts from scratch
		this.recalculateDivisionCounts();

		// Update chart after filtering
		setTimeout(() => {
			this.drawGradeChart();
		}, 100);
	}

	// Recalculate division counts from scratch based on excluded flags
	recalculateDivisionCounts() {
		if (!this.data) return;

		// Reset all division counts to 0
		for (const key in this.data) {
			if (this.data.hasOwnProperty(key) && key !== 'complete' && key !== '_meta') {
				const phaseData = this.data[key];
				if (phaseData?.current?.divisions) {
					// Initialize arrays if they don't exist
					if (!phaseData.current.divisions.data) {
						phaseData.current.divisions.data = new Array(5).fill(0);
					}
					if (!phaseData.current.divisions.raw) {
						phaseData.current.divisions.raw = new Array(5).fill(0);
					}

					// Reset to 0
					for (let i = 0; i < 5; i++) {
						phaseData.current.divisions.data[i] = 0;
						phaseData.current.divisions.raw[i] = 0;
					}

					// Count non-excluded students in each division
					const students = phaseData.current.students;
					if (students) {
						const processedStudentIds = new Set<string>();
						for (const studentId in students) {
							if (students.hasOwnProperty(studentId)) {
								const student = students[studentId];
								const studentIdKey = student.id || studentId;

								// Skip if already processed (avoid double counting)
								if (processedStudentIds.has(studentIdKey)) {
									continue;
								}

								// Only count non-excluded, assessed students
								if (
									!student.excluded &&
									!student.notAssessed &&
									student.division?.key !== undefined &&
									student.currentPhase == key
								) {
									const divIndex = 4 - student.division.key;
									if (divIndex >= 0 && divIndex < 5) {
										phaseData.current.divisions.data[divIndex]++;
										phaseData.current.divisions.raw[divIndex]++;
										processedStudentIds.add(studentIdKey);
									}
								}
							}
						}
					}
				}
			}
		}

		// If includeNotAssessed is true, add not-assessed students to emerging division (phase 1011, index 0)
		if (this.filterModel.includeNoneAssessed && this.data[1011]?.current?.divisions) {
			const notAssessedCount = this.countNotAssessedStudents();
			if (notAssessedCount > 0) {
				const emergingIndex = 4; // Emerging is at index 0
				this.data[1011].current.divisions.data[emergingIndex] += notAssessedCount;
				this.data[1011].current.divisions.raw[emergingIndex] += notAssessedCount;
			}
		}
	}

	removeFromCompleted() {
		for (const key in this.done) {
			if (this.done.hasOwnProperty('complete')) {
				for (const student in this.data['complete'].current.students) {
					if (this.data[key].current.students.hasOwnProperty(student)) {
						const studentChar = this.data[key].current.students[student].characteristics;
						for (const char in studentChar) {
							if (this.done.includes(char)) {
								this.data['complete'].current.students[student].excluded = true;
								//if (this.data[1011].current.students.hasOwnProperty(student)) {
								this.data['complete'].current.totalStudents -= 1;
								//this.data[1011].current.students[student].excluded = true;
								//	}
							} else {
								this.data['complete'].current.students[student].excluded = false;
								//if (this.data[1011].current.students.hasOwnProperty(student)) {
								this.data['complete'].current.totalStudents += 1;
								//this.data[1011].current.students[student].excluded = false;
								//	}
							}
						}
					}
				}
			}
		}
	}
	countCompleteNotExcluded(): number {
		let count = 0;
		const completeStudents = this.data?.['complete']?.current?.students;
		if (completeStudents) {
			for (const student in completeStudents) {
				if (completeStudents.hasOwnProperty(student) && !completeStudents[student].excluded) {
					count++;
				}
			}
		}
		this.completedPercent = this.calculatePercentage(count);
		return count;
	}
	updatedHistory(value: 'next' | 'previous'): void {
		console.log('currentHistory', this.currentHistoryIndex);
		if (value === 'next' && this.currentHistoryIndex < this.readingAchievementHistory.length - 1) {
			this.currentHistoryIndex++;
		} else if (value === 'previous' && this.currentHistoryIndex > 0) {
			this.currentHistoryIndex--;
		}
		this.changeHistory(this.currentHistoryIndex);
	}

	startHistoryChange(direction: 'next' | 'previous'): void {
		this.updatedHistory(direction);
		this.holdInterval = setInterval(() => {
			this.updatedHistory(direction);
		}, this.HOLD_DELAY);
	}

	stopHistoryChange(): void {
		if (this.holdInterval) {
			clearInterval(this.holdInterval);
			this.holdInterval = null;
		}
	}

	cancelRequest() {
		if (this.dataSubscription) {
			this.dataSubscription.unsubscribe();
			this.loading = false;
			this.LoaderService.setLoadingGrowthChartRaw(false);
			this.LoaderService.setLoadingGrowthChart(false); // Reset the loader set by getReadingAchievementPhases
		}
	}

	// Calculate all percentages from raw data, optionally including participantsWithNoScores
	// This matches the logic from reading-achievement-phases component
	calculatePercentages(data: any, includeNotAssessed: boolean = false): any {
		if (!data || !data.phases) {
			return data;
		}

		const modes = ['current'];
		// Deep copy phases to avoid modifying original data
		const calculatedPhases = JSON.parse(JSON.stringify(data.phases));
		modes.forEach((mode) => {
			if (includeNotAssessed && data.notAssessedCounts?.[mode]?.['students']) {
				// students is an object, not an array, so use Object.keys().length
				const students = data.notAssessedCounts[mode]['students'];
				const notAssessedCount = Object.keys(students).length;
				if (notAssessedCount > 0) {
					const divisionsData = calculatedPhases[1011][mode].divisions.data;
					const lastIndex = divisionsData.length - 1; //emerging data

					// Read original value directly from source data and add notAssessedCount
					const originalValue = data.phases[1011][mode].divisions.data[lastIndex] || 0;
					calculatedPhases[1011][mode].divisions.data[lastIndex] = originalValue + notAssessedCount; //add notAssessedCount to the last index
					console.log('notAssessedCount', notAssessedCount);
				}
			}

			// Step 2: Calculate total participants across all phases + complete
			// Now division[0] of phase 1011 already includes notAssessed if applicable
			let totalParticipantsAcrossAllPhases = 0;

			// Sum all phase divisions (1011, 1012, 1013) - this now includes notAssessed in phase 1011's division[0]
			Object.keys(calculatedPhases).forEach((phaseKey) => {
				if (phaseKey !== 'complete' && phaseKey !== '_meta') {
					const phase = calculatedPhases[phaseKey];
					if (phase[mode]?.divisions?.data) {
						const sumOfDivisions = phase[mode].divisions.data.reduce((sum: number, val: number) => sum + (val || 0), 0);
						totalParticipantsAcrossAllPhases += sumOfDivisions;
					}
				}
			});

			// Add complete phase count
			if (calculatedPhases['complete']?.[mode]?.students) {
				// students is an object (not array), so use Object.keys().length
				totalParticipantsAcrossAllPhases += Object.keys(calculatedPhases['complete'][mode].students).length || 0;
			} else if (calculatedPhases['complete']?.[mode]?.count) {
				totalParticipantsAcrossAllPhases += calculatedPhases['complete'][mode].count || 0;
			}

			// Step 3: Calculate percentages for each phase
			Object.keys(calculatedPhases).forEach((phaseKey) => {
				if (phaseKey !== 'complete' && phaseKey !== '_meta') {
					const phase = calculatedPhases[phaseKey];
					if (phase[mode]?.divisions?.data) {
						// Initialize percentage array if it doesn't exist
						if (!phase[mode].divisions.percentage) {
							phase[mode].divisions.percentage = [];
						}

						// Initialize raw array if it doesn't exist
						if (!phase[mode].divisions.raw) {
							phase[mode].divisions.raw = [];
						}

						// Calculate percentage for each division and ensure raw array matches data
						phase[mode].divisions.data.forEach((rawValue: number, index: number) => {
							// Update raw array to match data (important for when notAssessed is added)
							phase[mode].divisions.raw[index] = rawValue;

							// Calculate percentage
							phase[mode].divisions.percentage[index] =
								totalParticipantsAcrossAllPhases > 0
									? this.roundToTwoDecimals((rawValue / totalParticipantsAcrossAllPhases) * 100)
									: 0;
						});

						// Calculate totalRaw (sum of divisions)
						phase[mode].totalRaw = phase[mode].divisions.data.reduce((sum: number, val: number) => sum + (val || 0), 0);

						// Calculate totalPercent directly from raw data (not from summing rounded division percentages)
						// This avoids rounding errors that occur when summing already-rounded values
						phase[mode].totalPercent =
							totalParticipantsAcrossAllPhases > 0
								? this.roundToTwoDecimals((phase[mode].totalRaw / totalParticipantsAcrossAllPhases) * 100)
								: 0;
					}
				}
			});

			// Step 4: Calculate complete phase percentage
			if (calculatedPhases['complete']?.[mode]) {
				// students is an object (not array), so use Object.keys().length
				const completeCount = calculatedPhases['complete'][mode].students
					? Object.keys(calculatedPhases['complete'][mode].students).length
					: calculatedPhases['complete'][mode].count || 0;
				calculatedPhases['complete'][mode].percentage =
					totalParticipantsAcrossAllPhases > 0
						? this.roundToTwoDecimals((completeCount / totalParticipantsAcrossAllPhases) * 100)
						: 0;
			}

			// Step 5: Calculate "not assessed" percentage if including them
			if (includeNotAssessed && data.notAssessedCounts?.[mode]?.['students']) {
				// students is an object, not an array, so use Object.keys().length
				const students = data.notAssessedCounts[mode]['students'];
				const notAssessedCount = Object.keys(students).length;
				if (notAssessedCount > 0) {
					const notAssessedPercent =
						totalParticipantsAcrossAllPhases > 0
							? this.roundToTwoDecimals((notAssessedCount / totalParticipantsAcrossAllPhases) * 100)
							: 0;

					// Store in a convenient location for display
					if (!calculatedPhases['_notAssessed']) {
						calculatedPhases['_notAssessed'] = {};
					}
					if (!calculatedPhases['_notAssessed'][mode]) {
						calculatedPhases['_notAssessed'][mode] = {};
					}

					calculatedPhases['_notAssessed'][mode].count = notAssessedCount;
					calculatedPhases['_notAssessed'][mode].percentage = notAssessedPercent;
				}
			}
		});

		return {
			...data,
			phases: calculatedPhases,
		};
	}

	// Recalculate all data when toggling options
	recalculateData(): void {
		// Use originalData (full response with phases, characteristics, etc.)
		if (this.originalData) {
			this.calculatedData = this.calculatePercentages(this.originalData, this.includeNotAssessed);
			// Update data with calculated phases so template can access them
			// calculatedData.phases includes all phases (1011, 1012, 1013) and complete
			// data structure: { "1011": {...}, "1012": {...}, "1013": {...}, "complete": {...} }
			this.data = this.calculatedData.phases;
			// Update totals
			this.updateTotal();
			this.countCompleteNotExcluded();
		}
	}

	// Helper method to get not assessed students count
	getNotAssessedCount(mode: string = 'current'): number {
		if (!this.originalData?.notAssessedCounts?.[mode]?.['students']) {
			return 0;
		}
		const students = this.originalData.notAssessedCounts[mode]['students'];
		// students is an object, not an array, so use Object.keys().length
		return Object.keys(students).length;
	}

	// Helper method to round to 2 decimal places
	roundToTwoDecimals(value: number, decimals: number = 2): number {
		const factor = Math.pow(10, decimals);
		return Math.round(value * factor) / factor;
	}

	// Check if there are characteristics available for breakdown
	hasCharacteristicsForBreakdown(): boolean {
		return this.filterModel.characteristics && Object.keys(this.filterModel.characteristics).length > 0 && this.data;
	}

	// Get available characteristic keys
	getAvailableCharacteristics(): string[] {
		if (!this.filterModel.characteristics) {
			return [];
		}
		return Object.keys(this.filterModel.characteristics);
	}

	// Get display name for a characteristic key
	getCharacteristicDisplayName(characteristicKey: string): string {
		// Convert key to display name (e.g., 'grade' -> 'Grade', 'ethnicity' -> 'Ethnicity')
		const displayNames: { [key: string]: string } = {
			grade: 'Grade',
			ethnicity: 'Ethnicity',
			gender: 'Gender',
			race: 'Race',
			// Add more mappings as needed
		};

		return (
			displayNames[characteristicKey.toLowerCase()] ||
			characteristicKey
				.split(/(?=[A-Z])/)
				.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
				.join(' ')
		);
	}

	// Get characteristic breakdown data for a specific phase
	getCharacteristicBreakdownForPhase(phase: any, characteristicKey: string): any[] {
		if (!this.data || !phase || !characteristicKey) {
			return [];
		}

		const phaseData = this.data[phase.assessmentBookId];
		if (!phaseData?.current?.students) {
			return [];
		}

		const students = phaseData.current.students;
		const breakdownMap: { [value: string]: number[] } = {}; // [Emerging, Bronze, Silver, Gold, Platinum]

		// Initialize breakdown map with all characteristic values
		if (this.filterModel.characteristics[characteristicKey]) {
			this.filterModel.characteristics[characteristicKey].forEach((char: any) => {
				breakdownMap[char.name] = [0, 0, 0, 0, 0];
			});
		}

		// Count students by characteristic value and division
		for (const studentId in students) {
			if (students.hasOwnProperty(studentId)) {
				const student = students[studentId];
				const studentTargetPhase = phase.assessmentBookId;

				// Only count non-excluded students in the correct phase
				if (
					!student.excluded &&
					!student.notAssessed &&
					student.currentPhase == studentTargetPhase &&
					student.division?.key !== undefined
				) {
					const characteristicValue = student.characteristics?.[characteristicKey] || 'N/A';
					const divIndex = 4 - student.division.key; // Convert division key to array index (0-4)

					if (divIndex >= 0 && divIndex < 5) {
						if (!breakdownMap[characteristicValue]) {
							breakdownMap[characteristicValue] = [0, 0, 0, 0, 0];
						}
						breakdownMap[characteristicValue][divIndex]++;
					}
				}
			}
		}

		// Convert map to array format with percentages
		const result: any[] = [];
		for (const value in breakdownMap) {
			if (breakdownMap.hasOwnProperty(value)) {
				const counts = breakdownMap[value];
				const totalStudents = counts.reduce((sum, count) => sum + count, 0);

				// Calculate percentages for each division
				const percentages = counts.map((count) => {
					const total = this.getTotalStudentsForPhase(phase);
					return total > 0 ? this.roundToTwoDecimals((count / total) * 100) : 0;
				});

				result.push({
					value: value,
					counts: counts,
					percentages: percentages,
					totalStudents: totalStudents,
				});
			}
		}

		// Sort by value for consistent display
		return result.sort((a, b) => {
			if (a.value === 'N/A') return 1;
			if (b.value === 'N/A') return -1;
			return a.value.localeCompare(b.value);
		});
	}

	// Helper method to get total students for a phase (for percentage calculation)
	private getTotalStudentsForPhase(phase: any): number {
		if (!this.data || !phase) {
			return 0;
		}

		const phaseData = this.data[phase.assessmentBookId];
		if (!phaseData?.current?.divisions?.raw) {
			return 0;
		}

		// Sum all divisions for this phase
		return phaseData.current.divisions.raw.reduce((sum: number, val: number) => sum + (val || 0), 0);
	}

	ngAfterViewInit(): void {
		// Chart will be initialized when data is loaded
	}

	// Get grade breakdown data for chart and table
	getGradeBreakdownData(): any {
		if (!this.data || !this.filterModel.characteristics || !(this.filterModel.characteristics as any).grade) {
			return null;
		}

		const gradeCharacteristic = (this.filterModel.characteristics as any).grade;
		if (!Array.isArray(gradeCharacteristic) || gradeCharacteristic.length === 0) {
			return null;
		}

		// Initialize breakdown structure: { grade: { phase1: count, phase2: count, phase3: count, complete: count } }
		const breakdown: { [grade: string]: { phase1: number; phase2: number; phase3: number; complete: number } } = {};
		const allGrades: string[] = [];

		// Initialize all grades from characteristics
		gradeCharacteristic.forEach((gradeOption: any) => {
			const grade = gradeOption.name;
			if (grade !== 'N/A') {
				breakdown[grade] = { phase1: 0, phase2: 0, phase3: 0, complete: 0 };
				allGrades.push(grade);
			}
		});

		// Count students by grade and phase
		// Process Phase 1, 2, 3
		for (const phaseId of [1011, 1012, 1013]) {
			const phaseData = this.data[phaseId];
			if (!phaseData?.current?.students) continue;

			const students = phaseData.current.students;
			for (const studentId in students) {
				if (students.hasOwnProperty(studentId)) {
					const student = students[studentId];
					if (!student.excluded && !student.notAssessed && student.currentPhase == phaseId) {
						const grade = student.characteristics?.grade || 'N/A';
						if (grade !== 'N/A' && breakdown[grade]) {
							if (phaseId === 1011) breakdown[grade].phase1++;
							else if (phaseId === 1012) breakdown[grade].phase2++;
							else if (phaseId === 1013) breakdown[grade].phase3++;
						}
					}
				}
			}
		}

		// Process Complete phase
		if (this.data['complete']?.current?.students) {
			const completeStudents = this.data['complete'].current.students;
			for (const studentId in completeStudents) {
				if (completeStudents.hasOwnProperty(studentId)) {
					const student = completeStudents[studentId];
					if (!student.excluded) {
						const grade = student.characteristics?.grade || 'N/A';
						if (grade !== 'N/A' && breakdown[grade]) {
							breakdown[grade].complete++;
						}
					}
				}
			}
		}

		// Calculate totals and percentages
		const result: any[] = [];
		let allTotal = { phase1: 0, phase2: 0, phase3: 0, complete: 0 };

		allGrades.sort().forEach((grade) => {
			const counts = breakdown[grade];
			const total = counts.phase1 + counts.phase2 + counts.phase3 + counts.complete;

			// Calculate percentages based on total students in that grade
			const percentages = {
				phase1: total > 0 ? this.roundToTwoDecimals((counts.phase1 / total) * 100) : 0,
				phase2: total > 0 ? this.roundToTwoDecimals((counts.phase2 / total) * 100) : 0,
				phase3: total > 0 ? this.roundToTwoDecimals((counts.phase3 / total) * 100) : 0,
				complete: total > 0 ? this.roundToTwoDecimals((counts.complete / total) * 100) : 0,
			};

			result.push({
				grade,
				counts,
				percentages,
				total,
			});

			// Accumulate for ALL
			allTotal.phase1 += counts.phase1;
			allTotal.phase2 += counts.phase2;
			allTotal.phase3 += counts.phase3;
			allTotal.complete += counts.complete;
		});

		// Add ALL row
		const allGrandTotal = allTotal.phase1 + allTotal.phase2 + allTotal.phase3 + allTotal.complete;
		const allPercentages = {
			phase1: allGrandTotal > 0 ? this.roundToTwoDecimals((allTotal.phase1 / allGrandTotal) * 100) : 0,
			phase2: allGrandTotal > 0 ? this.roundToTwoDecimals((allTotal.phase2 / allGrandTotal) * 100) : 0,
			phase3: allGrandTotal > 0 ? this.roundToTwoDecimals((allTotal.phase3 / allGrandTotal) * 100) : 0,
			complete: allGrandTotal > 0 ? this.roundToTwoDecimals((allTotal.complete / allGrandTotal) * 100) : 0,
		};

		result.push({
			grade: 'ALL',
			counts: allTotal,
			percentages: allPercentages,
			total: allGrandTotal,
		});

		return result;
	}

	// Initialize or update the grade breakdown chart
	drawGradeChart(): void {
		if (!this.gradeChartContainer || !this.data) {
			return;
		}

		const breakdownData = this.getGradeBreakdownData();
		if (!breakdownData || breakdownData.length === 0) {
			return;
		}

		// Destroy existing chart if it exists and clear container
		if (this.gradeChart) {
			this.gradeChart.destroy();
			this.gradeChart = null;
		}

		// Clear the container
		const container = this.gradeChartContainer.nativeElement;
		if (container) {
			container.innerHTML = '';
		}

		// Prepare chart data
		const categories = breakdownData.map((item) => item.grade);
		const phase1Data = ['Phase I', ...breakdownData.map((item) => item.percentages.phase1)];
		const phase2Data = ['Phase II', ...breakdownData.map((item) => item.percentages.phase2)];
		const phase3Data = ['Phase III', ...breakdownData.map((item) => item.percentages.phase3)];
		const completeData = ['Complete', ...breakdownData.map((item) => item.percentages.complete)];

		// Create chart
		this.gradeChart = c3.generate({
			bindto: container,
			data: {
				columns: [phase1Data, phase2Data, phase3Data, completeData],
				type: 'bar',
				groups: [['Phase I', 'Phase II', 'Phase III', 'Complete']],
			},
			color: {
				pattern: ['#B65250', '#7D5FAA', '#435F90', '#fcc200'], // Phase I, Phase II, Phase III, Complete (order matches groups)
			},
			axis: {
				x: {
					type: 'category',
					categories: categories,
					label: {
						text: 'Grade',
						position: 'outer-center',
					},
				},
				y: {
					label: {
						text: '% Students',
						position: 'outer-middle',
					},
					min: 0,
					max: 100,
					padding: { top: 0, bottom: 0 },
					tick: {
						format: (d) => d + '%',
						values: [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100],
					},
				},
			},
			grid: {
				y: {
					show: true,
				},
			},
			bar: {
				width: {
					ratio: 0.6,
				},
			},
			legend: {
				show: true,
				position: 'top',
			},
			tooltip: {
				format: {
					value: (value) => value.toFixed(2) + '%',
				},
			},
		});
	}

	// Check if grade data is available
	hasGradeData(): boolean {
		if (!this.data || !this.filterModel.characteristics) {
			return false;
		}
		const gradeCharacteristic = (this.filterModel.characteristics as any).grade;
		return gradeCharacteristic && Array.isArray(gradeCharacteristic) && gradeCharacteristic.length > 0;
	}

	// Clear cache and reload data
	clearCacheAndReload(): void {
		// Use shared service method to get node and clear cache
		const nodeToReload = this.reportService.clearCacheAndGetNode(this.filterModel?.node);

		if (nodeToReload) {
			// Reload data
			this.loadData();
		}
	}

	getLastPulledFormatted(): string {
		if (this.filterModel?.node?.id) {
			return this.reportService.getLastPulledFormatted(this.filterModel.node.id);
		}
	}
}
